import React, { useState } from "react";

import { DatasetPurpose } from "src/api/types";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { useDatasets, useDeleteDataset } from "src/datasets/api/queries";
import { DatasetList } from "src/datasets/DatasetList/DatasetList";
import { separateFilenameExtension, useSchemas } from "src/datasets/utils";
import { toastActions } from "src/design-system/Toast/utils";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useSetEditDatasetId } from "src/router/SearchParams";
import { logger } from "src/utils/logger";

type DatasetSidePaneProps = {
  onRequestClose: () => void;
  onAssembleDataset: (purpose: DatasetPurpose) => void;
};

type ItemMode = "delete";
export const DatasetSidePane: React.FC<DatasetSidePaneProps> = ({
  onRequestClose,
  onAssembleDataset,
}) => {
  const { workspace, flow, version } = useAuthoringContext();
  const deleteDataset = useDeleteDataset(workspace?.base_url);
  const datasetsQuery = useDatasets({
    flowId: flow.id,
    baseUrl: workspace.base_url,
  });

  const [activeMode, setActiveMode] = useState<ItemMode | null>(null);
  const [activeDatasetId, setActiveDatasetId] = useState<string | null>(null);

  const schemas = useSchemas(version);

  const setActive = (id: string, mode: ItemMode) => {
    setActiveDatasetId(id);
    setActiveMode(mode);
  };

  const setEditDatasetId = useSetEditDatasetId();

  const activeDataset =
    activeDatasetId !== null && datasetsQuery.data
      ? datasetsQuery.data.find((d) => d.id === activeDatasetId)
      : null;

  const handleConfirmDelete = async () => {
    if (!activeDataset) {
      return;
    }

    try {
      await deleteDataset.mutateAsync(activeDataset.id);
      toastActions.success({
        title: `Successfully deleted ${
          separateFilenameExtension(activeDataset.name).name
        }!`,
      });

      setActiveMode(null);
      setActiveDatasetId(null);
    } catch (err) {
      logger.error("Error deleting test data.", err);
    }
  };

  return (
    <div className="flex h-full w-128 flex-1 items-center justify-center border-gray-200 border-l bg-white pt-4">
      <div className="flex h-full w-full flex-col justify-start overflow-auto align-start">
        <DatasetList
          datasetsQuery={datasetsQuery}
          schemas={schemas}
          onAssemble={onAssembleDataset}
          onDelete={(dataset) => setActive(dataset.id, "delete")}
          onRequestClose={onRequestClose}
          onSelect={(dataset) => setEditDatasetId(dataset.id)}
        />
      </div>
      <ConfirmationModal
        confirmationButtonText="Yes, delete"
        open={activeMode === "delete"}
        title={
          activeDataset
            ? `Delete ${separateFilenameExtension(activeDataset.name).name}`
            : ""
        }
        variant="danger"
        onClose={() => setActiveMode(null)}
        onConfirm={handleConfirmDelete}
      >
        <div className="font-inter-normal-12px text-gray-500">
          Are you sure you want to delete this test dataset file? This action
          cannot be undone.
        </div>
      </ConfirmationModal>
    </div>
  );
};
